import React, { useState, useEffect } from 'react'
import { useNavigation } from 'react-navi'
import { makeStyles, createStyles } from '@material-ui/styles'
import { Typography, TextField, Button } from '@material-ui/core';
import { PostData } from '../Service/service';

const RESEND_COOLDOWN = 30;

const useStyles = makeStyles((theme) => createStyles({
    root: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        height: '100vh',
        width: '100vw'
    },
    fontLarge: {
        fontSize: '9em',
        fontWeight: 'bold',
        color: 'black'
    }
}))

export default function LoginPage({ currentUser, authService }) {
    const [username, setUsername] = useState('superadmin');
    const [password, setPassword] = useState('');
    const [otp, setOtp] = useState('');
    const [step, setStep] = useState('credentials');
    const [txn, setTxn] = useState('');
    const [phoneMasked, setPhoneMasked] = useState('');
    const [info, setInfo] = useState('');
    const [error, setError] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [resendIn, setResendIn] = useState(0);
    const classes = useStyles();
    let navigation = useNavigation();

    useEffect(() => {
        if (resendIn <= 0) {
            return;
        }
        const t = setTimeout(() => setResendIn(resendIn - 1), 1000);
        return () => clearTimeout(t);
    }, [resendIn])

    // the api returns a plain string (or false) on failure, an object on success
    function toErrorMessage(resp) {
        if (resp === false) {
            return 'You are not allowed to login here';
        }
        if (typeof resp === 'string') {
            return resp;
        }
        if (resp && resp.status === false) {
            return resp.status_text;
        }
        return 'Login failed. Please try again.';
    }

    function storeTokenAndGo(resp) {
        authService.setTokenAfterLogin(resp)
            .then((r) => {
                setIsLoading(false)
                if (r === true) {
                    navigation.navigate('/hr')
                        .then(() => {
                            window.location.reload()
                        })
                } else {
                    setError(toErrorMessage(r))
                }
            })
    }

    function onSubmitCredentials(e) {
        e.preventDefault();

        if (username === '' || password === '') {
            setError('Please insert all fields');
            return;
        }

        setError('')
        setInfo('')
        setIsLoading(true)

        PostData('/login', { email: username, password: password })
            .then((resp) => {
                setIsLoading(false)

                if (resp && resp.twofa === true) {
                    setTxn(resp.txn)
                    setPhoneMasked(resp.phone_masked)
                    setInfo(resp.status_text)
                    setOtp('')
                    setResendIn(RESEND_COOLDOWN)
                    setStep('otp')
                    return;
                }

                // employee logins still come back with a token in one step
                if (resp && resp.token) {
                    setIsLoading(true)
                    storeTokenAndGo(resp)
                    return;
                }

                setError(toErrorMessage(resp))
            })
            .catch(() => {
                setIsLoading(false)
                setError('Could not reach the server. Please try again.')
            })
    }

    function onSubmitOtp(e) {
        e.preventDefault();

        if (otp === '') {
            setError('Please enter the OTP');
            return;
        }

        setError('')
        setInfo('')
        setIsLoading(true)

        PostData('/login/verify-otp', { txn: txn, otp: otp })
            .then((resp) => {
                if (resp && resp.token) {
                    storeTokenAndGo(resp)
                } else {
                    setIsLoading(false)
                    setError(toErrorMessage(resp))
                }
            })
            .catch(() => {
                setIsLoading(false)
                setError('Could not reach the server. Please try again.')
            })
    }

    function onResend() {
        setError('')
        setInfo('')
        setIsLoading(true)

        PostData('/login/resend-otp', { txn: txn })
            .then((resp) => {
                setIsLoading(false)

                if (resp && resp.twofa === true) {
                    setTxn(resp.txn)
                    setPhoneMasked(resp.phone_masked)
                    setInfo(resp.status_text)
                    setOtp('')
                    setResendIn(RESEND_COOLDOWN)
                } else {
                    setError(toErrorMessage(resp))
                }
            })
            .catch(() => {
                setIsLoading(false)
                setError('Could not reach the server. Please try again.')
            })
    }

    function onBackToLogin() {
        setStep('credentials')
        setTxn('')
        setOtp('')
        setPassword('')
        setPhoneMasked('')
        setError('')
        setInfo('')
        setResendIn(0)
    }

    return <React.Fragment>
        <div className={classes.root}>
            <div align="center">

                <img
                    src={require('./../assets/login-bg.jpg')}
                    style={{ width: '70%' }}
                />

                {step === 'credentials' ?
                    <form onSubmit={onSubmitCredentials}>
                        <div align="left" style={{ width: '50%' }}>
                            <TextField
                                required
                                error={Boolean(error)}
                                fullWidth
                                label="Username/Email"
                                className={classes.textField}
                                margin="normal"
                                variant="outlined"
                                value={username}
                                onChange={e => setUsername(e.target.value)}
                            />

                            <TextField
                                required
                                type="password"
                                fullWidth
                                error={Boolean(error)}
                                label="Password"
                                className={classes.textField}
                                margin="normal"
                                variant="outlined"
                                value={password}
                                onChange={e => setPassword(e.target.value)}
                            />

                            <div align="right">
                                <Button
                                    variant="contained"
                                    color="primary"
                                    type="submit"
                                    disabled={isLoading}
                                >
                                    Login
                                </Button>
                            </div>

                            {error !== '' ? <Typography style={{ color: 'red' }} variant="caption">{error}</Typography> : null}
                        </div>
                    </form>
                    :
                    <form onSubmit={onSubmitOtp}>
                        <div align="left" style={{ width: '50%' }}>

                            <Typography variant="body2" gutterBottom>
                                Enter the 6 digit OTP sent to {phoneMasked}
                            </Typography>

                            <TextField
                                required
                                autoFocus
                                fullWidth
                                error={Boolean(error)}
                                label="OTP"
                                className={classes.textField}
                                margin="normal"
                                variant="outlined"
                                inputProps={{ maxLength: 6, inputMode: 'numeric' }}
                                value={otp}
                                onChange={e => setOtp(e.target.value.replace(/\D/g, ''))}
                            />

                            <div align="right">
                                <Button
                                    onClick={onBackToLogin}
                                    disabled={isLoading}
                                >
                                    Back
                                </Button>

                                <Button
                                    onClick={onResend}
                                    disabled={isLoading || resendIn > 0}
                                >
                                    {resendIn > 0 ? 'Resend in ' + resendIn + 's' : 'Resend OTP'}
                                </Button>

                                <Button
                                    variant="contained"
                                    color="primary"
                                    type="submit"
                                    disabled={isLoading}
                                >
                                    Verify
                                </Button>
                            </div>

                            {info !== '' ? <Typography style={{ color: 'green' }} variant="caption">{info}</Typography> : null}
                            {error !== '' ? <Typography style={{ color: 'red' }} variant="caption">{error}</Typography> : null}
                        </div>
                    </form>
                }

            </div>
        </div>
    </React.Fragment>
}
